import { Divider } from "@material-ui/core";
import { Container, Grid } from "@mui/material";
import axios from "axios";
import {
  FormCard,
  FormEditor,
  FormLabel,
  FormUploadImage,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
} from "components";
import { HeaderTitle } from "layouts";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import {
  deleteAllValuesWebManagement,
  setValueWebManagement,
} from "redux/actions/webManagement";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import {
  createEditorContent,
  createEditorState,
  editorStateToHtml,
  getErrors,
  padArray,
} from "utils";

const ManajemenWeb = () => {
  const classes = globalStyles();
  const history = useHistory();
  const { webManagement } = useSelector(data => data);
  const dispatch = useDispatch();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loadingPage, setLoadingPage] = useState(false);
  const [payload, setPayload] = useState({
    about_us: createEditorContent(webManagement?.about_us),
    about_us_images: webManagement?.about_us_images || [],
    our_vision: createEditorContent(webManagement?.our_vision),
    our_vision_images: webManagement?.our_vision_images || [],
    publisher_images: webManagement?.publisher_images || [],
    delete_img_id: webManagement?.delete_img_id || [],
  });

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangeImageLogo = (value, key, index) => {
    setPayload(prev => {
      const existingItem = prev[key][index];
      const deleteId = existingItem?.web_management_image_id;

      return {
        ...prev,
        delete_img_id: deleteId
          ? [...(prev.delete_img_id || []), deleteId]
          : prev.delete_img_id || [],
        [key]: prev[key].map((img, idx) => (idx === index ? value : img)),
      };
    });
  };
  const handlePreview = () => {
    const { about_us, our_vision, ...restPayload } = payload;
    const previewPayload = {
      about_us: editorStateToHtml(about_us),
      our_vision: editorStateToHtml(our_vision),
      ...restPayload,
    };
    dispatch(setValueWebManagement(previewPayload));
    history.push("/admin/manajemen-web/preview");
  };

  const getDetail = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/publisher/web-management`, {
        headers,
      });
      const { data } = res?.data;
      const groupedImages = {
        about_us: [],
        our_vision: [],
        publisher: [],
      };
      data.images?.forEach(img => {
        if (groupedImages[img.type]) {
          groupedImages[img.type].push({
            image_url: img.image_name,
            image: null,
            ...img,
          });
        }
      });

      const emptyImage = {
        image: null,
        image_url: "",
        web_management_image_id: null,
      };
      setPayload(prev => ({
        ...prev,
        about_us: createEditorState(data.about_us),
        our_vision: createEditorState(data.our_vision),
        about_us_images: padArray({
          array: groupedImages?.about_us,
          length: 2,
          emptyObject: emptyImage,
        }),
        our_vision_images: padArray({
          array: groupedImages?.our_vision,
          length: 2,
          emptyObject: emptyImage,
        }),
        publisher_images: padArray({
          array: groupedImages?.publisher,
          length: 6,
          emptyObject: emptyImage,
        }),
      }));
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const formData = new FormData();
    const { about_us, our_vision, delete_img_id } = payload;
    const modifiedPayload = {
      about_us: editorStateToHtml(about_us),
      our_vision: editorStateToHtml(our_vision),
    };
    Object.keys(modifiedPayload).forEach(key => {
      formData.append(key, modifiedPayload[key]);
    });
    const imageKeys = [
      { stateKey: "about_us_images", formKey: "about_us_images" },
      { stateKey: "our_vision_images", formKey: "our_vision_images" },
      { stateKey: "publisher_images", formKey: "publisher_images" },
    ];
    imageKeys.forEach(({ stateKey, formKey }) => {
      const images = payload[stateKey];
      if (!Array.isArray(images)) return;
      images.forEach(({ image }) => {
        const isFile = image instanceof File || image instanceof Blob;
        formData.append(formKey, isFile ? image : "");
      });
    });
    formData.append("delete_img_id", JSON.stringify(delete_img_id));

    try {
      setLoadingPage(true);
      await axios.post(`${hardBaseUrl}/publisher/web-management`, formData, {
        headers,
      });
      ModalSuccess("Successfully save contents").then(() => {
        history.go(0);
        deleteAllValuesWebManagement();
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  useEffect(() => {
    const blankRedux =
      !webManagement?.about_us ||
      !webManagement?.about_us_images?.length ||
      !webManagement?.our_vision ||
      !webManagement?.our_vision_images?.length ||
      !webManagement?.publisher_images?.length ||
      !webManagement?.delete_img_id;
    if (blankRedux) {
      getDetail();
    }
  }, []);

  return (
    <Page className={classes.root} title="Web Management">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle title="Web Management" breadcrumbData={breadcrumbData} />
          <Divider className={classes.divider} />
          <SectionLabel title="About Us" subTitle="Information about us" />
          <FormCard>
            <FormLabel label="Photo" required />
            <Grid container columnSpacing={2} rowSpacing={1} mb={2}>
              {payload?.about_us_images?.map((item, index) => (
                <Grid item key={index}>
                  <FormUploadImage
                    imageUrl={item?.image_url}
                    setImageLogo={val =>
                      handleChangeImageLogo(val, "about_us_images", index)
                    }
                    noLabel
                    caption="Image"
                    height={200}
                    width={200}
                    fileSize={1}
                  />
                </Grid>
              ))}
            </Grid>
            <FormLabel label="Contents" required />
            <FormEditor
              editorState={payload?.about_us}
              onEditorStateChange={val => handleChangePayload(val, "about_us")}
              height={300}
            />
          </FormCard>
          <SectionLabel
            title="Our Vision"
            subTitle="This is the vision of your company"
          />
          <FormCard>
            <FormLabel label="Photo" required />
            <Grid container columnSpacing={2} rowSpacing={1} mb={2}>
              {payload?.our_vision_images?.map((item, index) => (
                <Grid item key={index}>
                  <FormUploadImage
                    imageUrl={item?.image_url}
                    setImageLogo={val =>
                      handleChangeImageLogo(val, "our_vision_images", index)
                    }
                    noLabel
                    caption="Image"
                    height={200}
                    width={200}
                    fileSize={1}
                  />
                </Grid>
              ))}
            </Grid>
            <FormLabel label="Contents" required />
            <FormEditor
              editorState={payload?.our_vision}
              onEditorStateChange={val =>
                handleChangePayload(val, "our_vision")
              }
              height={300}
            />
          </FormCard>
          <SectionLabel
            title="Publisher Photo"
            subTitle="This photo will be displayed on the landing page"
          />
          <FormCard>
            <FormLabel label="Photo" required />
            <Grid container columnSpacing={2} rowSpacing={1}>
              {payload?.publisher_images?.map((item, index) => (
                <Grid item key={index}>
                  <FormUploadImage
                    imageUrl={item?.image_url}
                    setImageLogo={val =>
                      handleChangeImageLogo(val, "publisher_images", index)
                    }
                    noLabel
                    caption="Image"
                    height={200}
                    width={200}
                    fileSize={1}
                  />
                </Grid>
              ))}
            </Grid>
          </FormCard>
          <Grid container justifyContent="right" columnSpacing={1} my={2}>
            <Grid item>
              <PrimaryButton label="Preview" onClick={handlePreview} />
            </Grid>
            <Grid item>
              <PrimaryButton label="Save" onClick={handleSubmit} />
            </Grid>
          </Grid>
        </Container>
      )}
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Web Management",
    active: true,
  },
];
export default ManajemenWeb;
